import React, { useEffect, useState } from "react";
import {
  Link,
  Switch,
  Route,
  Redirect,
  useHistory,
  useLocation,
} from "react-router-dom";
import { MenuOutlined, CloseOutlined } from "@material-ui/icons";
import { useParent } from "../../../context/ParentContext";
import { baseUrl, config } from "../../../services/config";
import { ParentPermissionChecker } from "../../../services/ParentPermissionChecker";
import ParentDashboard from "./ParentDashboard";
import MonthlyStudentAttendance from "./MonthlyStudentAttendance/MonthlyStudentAttendance";
import ExamResults from "./ExamResults/ExamResults";
import FeeStatus from "./FeeStatus/FeeStatus";
import AnnouncedDateSheets from "./AnnouncedDateSheets/AnnouncedDateSheets";
import ProgressionCharts from "./ProgressionCharts/ProgressionCharts";
import SchoolNotifications from "./SchoolNotifications/SchoolNotifications";
import SchoolResources from "./SchoolResources/SchoolResources";
import StudentHomeWork from "./StudentHomeWork/StudentHomeWork";
import StudentProfile from "./StudentProfile/StudentProfile.js";
import TeacherChat from "./TeacherChat";
import "./parent-layout.css";

const CHAT_UNREAD_REFRESH_INTERVAL = 10000;

const NAV_LINKS = [
  {
    to: "/parent/dashboard",
    label: "Dashboard",
    permission: "parent.view_dashboard",
  },
  {
    to: "/parent/student-profile",
    label: "Student Profile",
    permission: "parent.view_dashboard",
  },
  {
    to: "/parent/student-attendance",
    label: "Monthly Student Attendance",
    permission: "parent.view_attendance",
  },
  {
    to: "/parent/student-home-work",
    label: "Student Home Work",
    permission: "parent.view_dashboard",
  },
  {
    to: "/parent/date-sheets",
    label: "Announced Date Sheets",
    permission: "parent.view_results",
  },
  {
    to: "/parent/exam-results",
    label: "Exam Results",
    permission: "parent.view_results",
  },
  {
    to: "/parent/progression-charts",
    label: "Progression Charts",
    permission: "parent.view_results",
  },
  {
    to: "/parent/school-notifications",
    label: "School Notifications",
    permission: "parent.view_dashboard",
  },
  {
    to: "/parent/school-resources",
    label: "School Resources",
    permission: "parent.view_dashboard",
  },
  {
    to: "/parent/teacher-chat",
    label: "Teacher Chat",
    permission: "parent.view_dashboard",
  },
  {
    to: "/parent/fee-status",
    label: "Fee Status",
    permission: "parent.view_fee",
  },
];

// Parse a #rgb / #rrggbb colour into [r, g, b]; null when it isn't valid hex.
function parseHex(hex) {
  const value = String(hex || "").replace("#", "");
  const full =
    value.length === 3
      ? value
          .split("")
          .map((char) => char + char)
          .join("")
      : value;
  const parsed = parseInt(full, 16);
  if (full.length !== 6 || Number.isNaN(parsed)) return null;
  return [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255];
}

// Build a translucent variant of a theme colour for accent highlights.
function softColor(hex, alpha) {
  const rgb = parseHex(hex);
  if (!rgb) return `rgba(253, 240, 18, ${alpha})`;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

// Mix a theme colour toward white, for the light end of the page hero gradient.
function lighten(hex, amount) {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const mix = (channel) => Math.round(channel + (255 - channel) * amount);
  return `rgb(${mix(rgb[0])}, ${mix(rgb[1])}, ${mix(rgb[2])})`;
}

const getStudentId = (child) =>
  child?.student?.id || child?.student_id || child?.id || null;

export default function ParentLayout() {
  const {
    childList,
    selectedChild,
    setSelectedChild,
    unreadStatusCount,
    unreadResultCount,
    unreadFeeCount,
    unreadGeneralCount,
  } = useParent();
  const history = useHistory();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const selectedStudentId = getStudentId(selectedChild);

  const accentColor = config?.navBarColor || "#fdf012";
  const sidebarBg = config?.sideBarBgColor || "#1b1b28";
  const sidebarColor = config?.sideBarColor || "#121291";
  const studentPictureUrl = selectedChild?.student?.picture
    ? `${baseUrl.replace(/\/api\/?$/, "")}/storage/student/${
        selectedChild.student.picture
      }`
    : "";
  const themeVars = {
    "--parent-sidebar-bg": sidebarBg,
    "--parent-sidebar-hover": config?.sideBarHoverColor || "#1b1b28",
    "--parent-accent": accentColor,
    "--parent-accent-soft": softColor(accentColor, 0.08),
    "--parent-brand-color": config?.btnColor || sidebarColor,
    // Page hero (head section) gradient, shared by every parent portal page.
    "--parent-hero-bg": `linear-gradient(125deg, ${sidebarBg} 0%, ${sidebarColor} 68%, ${lighten(
      sidebarColor,
      0.22,
    )} 100%)`,
  };

  function handleLogout() {
    localStorage.removeItem("parentToken");
    localStorage.removeItem("parentUser");
    localStorage.removeItem("parentChildren");
    history.push("/parent/login");
  }

  const isActive = (path) => location.pathname === path;

  // Close the sidebar on route change (mobile off-canvas menu).
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const loadUnreadChatCount = async () => {
      if (!selectedStudentId) {
        if (!cancelled) setUnreadChatCount(0);
        return;
      }

      try {
        const token = localStorage.getItem("parentToken");
        const response = await fetch(`${baseUrl}/conversations`, {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const result = await response.json();
        if (!response.ok) return;

        const unreadTotal = (Array.isArray(result?.payload)
          ? result.payload
          : []
        )
          .filter(
            (conversation) =>
              Number(conversation.student_id) === Number(selectedStudentId),
          )
          .reduce(
            (total, conversation) =>
              total + Number(conversation.unread_count || 0),
            0,
          );

        if (!cancelled) setUnreadChatCount(unreadTotal);
      } catch (error) {
        // Keep the last known sidebar count if a background refresh fails.
      }
    };

    loadUnreadChatCount();
    const intervalId = window.setInterval(
      loadUnreadChatCount,
      CHAT_UNREAD_REFRESH_INTERVAL,
    );

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [selectedStudentId]);

  return (
    <div className="parent-layout" style={themeVars}>
      {/* ── Sidebar ───────────────────────────────────────────── */}
      <aside className={`parent-sidebar${sidebarOpen ? " open" : ""}`}>
        {/* Brand */}
        <div className="parent-sidebar-brand">
          <div>
            <div className="parent-sidebar-brand-title">Parent Portal</div>
            <div className="parent-sidebar-brand-subtitle">
              School Management System
            </div>
          </div>
          <button
            type="button"
            className="parent-sidebar-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <CloseOutlined fontSize="small" />
          </button>
        </div>

        {/* Nav */}
        <nav className="parent-sidebar-nav">
          {NAV_LINKS.map((link) => {
            const allowed = ParentPermissionChecker(link.permission);
            if (!allowed) return null;
            const unreadCount =
              link.to === "/parent/student-attendance"
                ? unreadStatusCount
                : link.to === "/parent/exam-results"
                ? unreadResultCount
                : link.to === "/parent/fee-status"
                ? unreadFeeCount
              : link.to === "/parent/school-notifications"
              ? unreadGeneralCount
              : link.to === "/parent/teacher-chat"
              ? unreadChatCount
              : 0;
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`parent-sidebar-link${
                  isActive(link.to) ? " active" : ""
                }`}
              >
                <span>{link.label}</span>
                {unreadCount > 0 && (
                  <span
                    className="parent-sidebar-unread-count"
                    aria-label={`${unreadCount} unread notifications`}
                  >
                    {unreadCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="parent-sidebar-logout">
          <button onClick={handleLogout}>Sign Out</button>
        </div>
      </aside>

      {/* ── Mobile overlay ────────────────────────────────────── */}
      <div
        className={`parent-layout-overlay${sidebarOpen ? " open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* ── Main area ─────────────────────────────────────────── */}
      <div className="parent-main">
        {/* Top nav */}
        <header className="parent-header">
          <div className="parent-header-left">
            <button
              type="button"
              className="parent-header-menu-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <MenuOutlined />
            </button>
            {selectedChild && (
              <span className="parent-header-avatar">
                <span aria-hidden="true">
                  {(selectedChild.student?.name || "Student")
                    .trim()
                    .charAt(0)
                    .toUpperCase()}
                </span>
                {studentPictureUrl && (
                  <img
                    key={studentPictureUrl}
                    src={studentPictureUrl}
                    alt={`${selectedChild.student?.name || "Student"} profile`}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                )}
              </span>
            )}
            <span className="parent-header-title">
              {selectedChild
                ? `${selectedChild.student?.name || "Student"} — ${selectedChild
                    .student?.student_class?.name || ""} ${selectedChild.student
                    ?.global_section?.name || ""}`
                : "No child selected"}
            </span>
          </div>

          {/* Child selector */}
          {childList.length > 1 && (
            <select
              className="parent-header-select"
              value={selectedChild?.id || ""}
              onChange={(e) => {
                const child = childList.find(
                  (c) => String(c.id) === String(e.target.value),
                );
                setSelectedChild(child || null);
              }}
            >
              {childList.map((child) => (
                <option key={child.id} value={child.id}>
                  {child.student?.name} — {child.student?.student_class?.name}
                </option>
              ))}
            </select>
          )}
        </header>

        {/* Page content */}
        <main className="parent-page-content">
          <Switch>
            <Route path="/parent/dashboard" component={ParentDashboard} />
            <Route path="/parent/student-profile" component={StudentProfile} />
            <Route
              path="/parent/student-attendance"
              component={MonthlyStudentAttendance}
            />
            <Route
              path="/parent/student-home-work"
              component={StudentHomeWork}
            />
            <Route path="/parent/exam-results" component={ExamResults} />
            <Route
              path="/parent/progression-charts"
              component={ProgressionCharts}
            />
            <Route
              path="/parent/school-notifications"
              component={SchoolNotifications}
            />
            <Route
              path="/parent/school-resources"
              component={SchoolResources}
            />
            <Route path="/parent/teacher-chat" component={TeacherChat} />
            <Route path="/parent/date-sheets" component={AnnouncedDateSheets} />
            <Route path="/parent/fee-status" component={FeeStatus} />
            <Redirect from="/parent" exact to="/parent/dashboard" />
          </Switch>
        </main>

        <footer className="parent-footer">
          <div className="parent-footer-brand">
            <span className="parent-footer-mark" aria-hidden="true">
              <img src="/media/logos/TVO_Logo.webp" alt="" />
            </span>
            <span className="parent-footer-powered">Powered by</span>
            <a
              href="https://tvoxel.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="parent-footer-company"
            >
              Tvoxel Technologies
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}
